import React, { useCallback, useEffect, useMemo, useState } from "react";
import toastr from "toastr";
import { FaBan, FaCheck, FaEdit, FaSpinner, FaTimes, FaUserShield, FaUsers } from "react-icons/fa";
import { Pagination } from "../../components";
import BrokerAlert from "../../components/Broker/BrokerAlert";
import BrokerAdminLabel from "../../components/Broker/BrokerAdminLabel";
import BrokerEmptyState from "../../components/Broker/BrokerEmptyState";
import BrokerSkeleton from "../../components/Broker/BrokerSkeleton";
import FilterBar from "../../components/Broker/FilterBar";
import { InputBar, SelectDropdown } from "../../components/Broker/FilterControls";
import { brokerAdminStore } from "./brokerAdminStore";
import { BrokerService, getBrokerProfile } from "./BrokerService";

const EMPTY_FORM = { name: "", email: "", status: "active" };
const DEFAULT_TEAM_FILTERS = { status: "", name: "", email: "" };
const DEFAULT_TEAM_SORT = { sortBy: "name", sortAsc: true };
const TEAM_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "invited", label: "Invited" },
  { value: "disabled", label: "Disabled" },
];
const TEAM_STATUS_VALUES = TEAM_STATUS_OPTIONS.map((option) => option.value);
// Separate Name and Email filters (no combined search) — each maps to its own
// caseIgnore[...] backend filter on the broker-admin grid.
const TEAM_OPTIONAL_FILTERS = [
  {
    key: "name",
    label: "Name",
    control: "text",
    placeholder: "Member name",
    id: "broker-team-name-filter",
    testId: "broker-team-filter-name",
    isActive: (filters) => Boolean(filters.name?.trim()),
  },
  {
    key: "email",
    label: "Email",
    control: "text",
    placeholder: "name@company.com",
    id: "broker-team-email-filter",
    testId: "broker-team-filter-email",
    isActive: (filters) => Boolean(filters.email?.trim()),
  },
];
const TEAM_DEFAULT_OPTIONAL_KEYS = ["name", "email"];
const TEAM_SORT_FIELD_OPTIONS = [
  { value: "name", label: "Name" },
  { value: "email", label: "Email" },
  { value: "status", label: "Status" },
  { value: "primaryAdmin", label: "Access" },
  { value: "createdAt", label: "Created" },
  { value: "lastLoginAt", label: "Last sign-in" },
];

const normalizeDomain = (value) => String(value || "").trim().toLowerCase().replace(/^@/, "");

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "Not available";
  return value;
};

const formatDate = (value) => {
  if (!value) return "Not available";
  const numeric = Number(value);
  const date = Number.isFinite(numeric)
    ? new Date(numeric < 1000000000000 ? numeric * 1000 : numeric)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const formatDateTime = (value) => {
  if (!value) return "Not available";
  const numeric = Number(value);
  const date = Number.isFinite(numeric)
    ? new Date(numeric < 1000000000000 ? numeric * 1000 : numeric)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const hasDateValue = (value) => {
  if (!value) return false;
  const numeric = Number(value);
  const date = Number.isFinite(numeric)
    ? new Date(numeric < 1000000000000 ? numeric * 1000 : numeric)
    : new Date(value);
  return !Number.isNaN(date.getTime());
};

const titleCase = (value) => String(value || "unknown").replace(/[_-]+/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

const initials = (name, email) => {
  const source = name || email || "Member";
  const parts = String(source).split(/[^A-Za-z0-9]+/).filter(Boolean);
  return (parts.length ? parts.slice(0, 2).map((part) => part[0]).join("") : "M").toUpperCase();
};

const getAdminId = (admin) => admin?._id || admin?.id || "";
const toCsvArray = (value) => String(value || "").split(",").filter(Boolean);
const caseIgnore = (value) => `caseIgnore[${String(value).trim()}]`;
const inFilter = (values) => `in[${values.join(",")}]`;

// Build the broker-admin grid query — separate name/email caseIgnore filters,
// status in[...], plus server-side sort + pagination.
const toTeamApiParams = (filters, sort, page, rowsPerPage, brokerId) => {
  const params = {
    brokerId,
    page,
    rows: rowsPerPage,
    sortBy: sort.sortBy,
    sortAsc: sort.sortAsc,
  };
  if (filters.name?.trim()) params.name = caseIgnore(filters.name);
  if (filters.email?.trim()) params.email = caseIgnore(filters.email);
  const statuses = toCsvArray(filters.status);
  if (statuses.length) params.status = inFilter(statuses);
  return params;
};
const normalizeStatus = (status) => {
  const normalized = String(status || "active").toLowerCase();
  return TEAM_STATUS_VALUES.includes(normalized) ? normalized : "active";
};
const accessLabel = (admin) => admin?.primaryAdmin ? "Primary member" : "Team member";

const sortToValue = (sort) => `${sort.sortBy}:${sort.sortAsc ? "asc" : "desc"}`;

const valueToSort = (value) => {
  const [sortBy, direction] = String(value || "").split(":");
  return {
    sortBy: sortBy || DEFAULT_TEAM_SORT.sortBy,
    sortAsc: direction !== "desc",
  };
};

const getErrorMessage = (error, fallback = "Something went wrong.") => {
  if (typeof error === "string" && error.trim()) return error;
  if (typeof error?.message === "string" && error.message.trim()) return error.message;
  return fallback;
};

const MemberStatus = ({ status }) => {
  const normalized = normalizeStatus(status);
  const StatusIcon = normalized === "active" ? FaCheck : normalized === "disabled" ? FaBan : FaUserShield;
  return (
    <span className={`broker-team-status broker-team-status--${normalized}`}>
      <StatusIcon aria-hidden="true" />
      {titleCase(normalized)}
    </span>
  );
};

const DetailItem = ({ label, children }) => (
  <div className="broker-team-dialog__detail">
    <span>{label}</span>
    <strong>{children || "Not available"}</strong>
  </div>
);

const BrokerTeam = () => {
  const profile = getBrokerProfile();
  const [broker, setBroker] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [total, setTotal] = useState(0);
  // Full roster (rows=-1) backs the app-wide admin-label store, current-admin
  // detection, and "Last sign-in" column visibility. The displayed `admins`
  // list is the server-filtered/paginated page.
  const [roster, setRoster] = useState([]);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [canManageTeam, setCanManageTeam] = useState(Boolean(profile?.canManageTeam));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionId, setActionId] = useState("");
  const [pageError, setPageError] = useState("");
  const [formError, setFormError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [filters, setFilters] = useState(DEFAULT_TEAM_FILTERS);
  const [sort, setSort] = useState(DEFAULT_TEAM_SORT);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Full roster + broker detail. Feeds the app-wide admin-label store and
  // current-admin detection. Refreshed after mutations so labels stay current.
  const loadRoster = useCallback(async () => {
    const latestProfile = getBrokerProfile() || {};
    const [brokerResponse, rosterResponse] = await Promise.all([
      latestProfile.brokerId ? BrokerService.brokerDetail(latestProfile.brokerId).catch(() => null) : null,
      BrokerService.team(latestProfile.brokerId),
    ]);
    const nextBroker = brokerResponse?.data || {};
    const nextRoster = Array.isArray(rosterResponse?.data?.rows) ? rosterResponse.data.rows : [];
    const current = nextRoster.find((admin) => getAdminId(admin) === latestProfile.brokerAdminId) || null;
    setBroker(nextBroker);
    setRoster(nextRoster);
    setCurrentAdmin(current);
    brokerAdminStore.setAdmins(nextRoster);
    setCanManageTeam(Boolean(latestProfile.isPrimaryAdmin || latestProfile.canManageTeam));
  }, []);

  // Displayed list — server-filtered / sorted / paginated via the broker-admin grid.
  const loadTeam = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setPageError("");
    try {
      const latestProfile = getBrokerProfile() || {};
      const params = toTeamApiParams(filters, sort, page, rowsPerPage, latestProfile.brokerId);
      const teamResponse = await BrokerService.teamGrid(params);
      const nextAdmins = Array.isArray(teamResponse?.data?.rows) ? teamResponse.data.rows : [];
      setAdmins(nextAdmins);
      setTotal(Number(teamResponse?.data?.total || 0));
    } catch (error) {
      const message = getErrorMessage(error, "Unable to load your team.");
      setPageError(message);
      toastr.error(message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filters, sort, page, rowsPerPage]);

  // Team (broker-admin grid) is primary-admin only; skip for everyone else so
  // the no-access state renders without a 401 logout.
  useEffect(() => {
    if (!getBrokerProfile()?.isPrimaryAdmin) { setLoading(false); return; }
    loadRoster().catch(() => {});
  }, [loadRoster]);

  useEffect(() => {
    if (!getBrokerProfile()?.isPrimaryAdmin) return;
    loadTeam();
  }, [loadTeam]);

  const emailDomain = normalizeDomain(broker?.emailDomain);
  const currentAdminId = currentAdmin?._id || profile?.brokerAdminId;

  const buildTeamUpdatePayload = useCallback((admin, overrides = {}) => {
    const next = { ...(admin || {}), ...(overrides || {}) };
    const name = String(next.name || next.email || "").trim();
    const email = String(next.email || "").trim().toLowerCase();
    return {
      ...next,
      _id: getAdminId(next),
      brokerId: next.brokerId || broker?._id || profile?.brokerId,
      name,
      email,
      status: normalizeStatus(next.status),
      primaryAdmin: Boolean(next.primaryAdmin),
      role: next.role,
      createdBy: next.createdBy,
      createdAt: next.createdAt,
      lastLoginAt: next.lastLoginAt,
    };
  }, [broker?._id, profile?.brokerId]);

  // Filtering / sorting / pagination are all server-side now — render the page
  // exactly as the broker-admin grid returns it.
  const pagedAdmins = admins;
  const hasLastSignInData = useMemo(() => roster.some((admin) => hasDateValue(admin?.lastLoginAt)), [roster]);
  const teamSortFieldOptions = useMemo(() => (
    hasLastSignInData
      ? TEAM_SORT_FIELD_OPTIONS
      : TEAM_SORT_FIELD_OPTIONS.filter((option) => option.value !== "lastLoginAt")
  ), [hasLastSignInData]);
  const getTeamRowTestId = useCallback((row, index) => (
    String(row?.email || getAdminId(row) || `member-${index + 1}`).replace(/[^A-Za-z0-9_-]+/g, "-").toLowerCase()
  ), []);

  // Keep the page in range as filters shrink the result set.
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(total / rowsPerPage));
    if (page > maxPage) setPage(maxPage);
  }, [total, rowsPerPage, page]);

  useEffect(() => {
    if (!hasLastSignInData && sort.sortBy === "lastLoginAt") setSort(DEFAULT_TEAM_SORT);
  }, [hasLastSignInData, sort.sortBy]);

  const openAddModal = () => { setEditing(null); setForm(EMPTY_FORM); setFormError(""); setModalOpen(true); };
  const openEditModal = useCallback((admin) => {
    setEditing(admin);
    setForm({ name: admin?.name || "", email: admin?.email || "", status: normalizeStatus(admin?.status) });
    setFormError("");
    setModalOpen(true);
  }, []);
  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError("");
  };

  useEffect(() => {
    if (!modalOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key !== "Escape" || saving) return;
      setModalOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      setFormError("");
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [modalOpen, saving]);

  const validateForm = () => {
    const name = form.name.trim();
    const email = form.email.trim().toLowerCase();
    if (!name) return "Name is required.";
    if (!email) return "Email is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Enter a valid email address.";
    if (emailDomain && !email.endsWith(`@${emailDomain}`)) return `Email must use @${emailDomain}.`;
    const selectedStatus = String(form.status || "").toLowerCase();
    if (!TEAM_STATUS_VALUES.includes(selectedStatus)) return "Choose a valid member status.";
    if (editing && selectedStatus === "disabled") {
      if (getAdminId(editing) === currentAdminId) return "You cannot disable your own member access.";
      if (editing.primaryAdmin) return "Primary member access cannot be disabled from the broker portal.";
    }
    const editingId = getAdminId(editing);
    const duplicate = admins.some((admin) => admin.email?.trim().toLowerCase() === email && getAdminId(admin) !== editingId);
    if (duplicate) return "This email is already on your team.";
    return "";
  };

  const saveMember = async () => {
    const validationError = validateForm();
    if (validationError) { setFormError(validationError); return; }
    setSaving(true);
    setFormError("");
    const payload = editing ? buildTeamUpdatePayload(editing, {
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      status: form.status,
    }) : {
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      status: normalizeStatus(form.status),
    };
    try {
      if (editing) {
        await BrokerService.updateTeamAdmin(getAdminId(editing), payload);
        toastr.success("Team member updated.");
      } else {
        await BrokerService.addTeamAdmin(payload);
        toastr.success("Team member added.");
      }
      setModalOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      await Promise.all([loadRoster().catch(() => {}), loadTeam({ silent: true })]);
    } catch (error) {
      const message = getErrorMessage(error, "Unable to save team member.");
      setFormError(message);
      toastr.error(message);
    } finally {
      setSaving(false);
    }
  };

  const updateMemberStatus = useCallback(async (admin, nextStatus) => {
    const adminId = getAdminId(admin);
    const isActive = normalizeStatus(admin.status) === "active";
    if (adminId === currentAdminId && nextStatus === "disabled") {
      toastr.error("You cannot disable your own account.");
      return;
    }
    setActionId(adminId);
    try {
      await BrokerService.updateTeamAdmin(adminId, buildTeamUpdatePayload(admin, { status: nextStatus }));
      toastr.success(nextStatus === "disabled" && isActive ? "Team member disabled." : "Team member restored.");
      await Promise.all([loadRoster().catch(() => {}), loadTeam({ silent: true })]);
    } catch (error) {
      toastr.error(getErrorMessage(error, "Unable to update team member."));
    } finally {
      setActionId("");
    }
  }, [buildTeamUpdatePayload, currentAdminId, loadRoster, loadTeam]);

  const renderTeamActions = useCallback((row) => {
    const adminId = getAdminId(row);
    const busy = actionId === adminId;
    const rowStatus = normalizeStatus(row?.status);
    const isActive = rowStatus === "active";
    const enableActionLabel = rowStatus === "disabled" ? "Restore member" : "Activate member";
    const isSelf = adminId === currentAdminId;
    const isPrimary = Boolean(row?.primaryAdmin);
    const label = row?.name || row?.email || "member";
    return (
      <div className="broker-grid-actions">
        <button
          type="button"
          className="broker-grid-action"
          onClick={() => openEditModal(row)}
          title="Edit member"
          aria-label={`Edit ${label}`}
        >
          <FaEdit aria-hidden="true" />
        </button>
        {isActive ? (
          <button
            type="button"
            className="broker-grid-action"
            onClick={() => updateMemberStatus(row, "disabled")}
            disabled={busy || isSelf || isPrimary}
            title={isSelf ? "You cannot disable your own member access" : isPrimary ? "Primary members cannot be disabled here" : "Disable member"}
            aria-label={`Disable ${label}`}
          >
            {busy ? <FaSpinner className="broker-spin" aria-hidden="true" /> : <FaBan aria-hidden="true" />}
          </button>
        ) : (
          <button
            type="button"
            className="broker-grid-action"
            onClick={() => updateMemberStatus(row, "active")}
            disabled={busy}
            title={enableActionLabel}
            aria-label={`${enableActionLabel.replace(" member", "")} ${label}`}
          >
            {busy ? <FaSpinner className="broker-spin" aria-hidden="true" /> : <FaCheck aria-hidden="true" />}
          </button>
        )}
      </div>
    );
  }, [actionId, currentAdminId, openEditModal, updateMemberStatus]);

  const updateFilters = useCallback((nextFilters) => {
    setPage(1);
    setFilters({ ...DEFAULT_TEAM_FILTERS, ...(nextFilters || {}) });
  }, []);

  const clearFilters = useCallback(() => {
    setPage(1);
    setFilters(DEFAULT_TEAM_FILTERS);
  }, []);

  const editingIsSelf = editing ? getAdminId(editing) === currentAdminId : false;
  const editingIsPrimary = Boolean(editing?.primaryAdmin);
  const statusLocked = Boolean(editing && (editingIsSelf || editingIsPrimary));
  const statusLockedHint = editingIsSelf
    ? "You cannot disable your own member access."
    : "Primary member status is controlled by FV Bank team.";

  if (!loading && !canManageTeam) {
    return (
      <div className="broker-page broker-team-page">
        <header className="broker-page-header">
          <p>Team</p>
          <h1>Team is managed by your primary member</h1>
          <span className="broker-page-header__sub">
            You can create and track applications. Adding or removing team members is handled by your primary broker contact.
          </span>
        </header>
        <BrokerEmptyState
          icon={FaUserShield}
          title="No team access on this account"
          description="You still have full access to the dashboard and applications. Ask your primary contact to manage portal members."
        />
      </div>
    );
  }

  return (
    <div className="broker-page broker-team-page">
      {pageError ? (
        <BrokerAlert type="error" title="Team could not be loaded" actionLabel="Retry" onAction={() => loadTeam()}>
          {pageError}
        </BrokerAlert>
      ) : null}

      <section className="broker-grid-panel">
        <FilterBar
          ariaLabel="Broker team filters"
          filters={filters}
          onChange={updateFilters}
          onClear={clearFilters}
          onRefresh={() => loadTeam()}
          refreshing={loading}
          onNewApplication={openAddModal}
          primaryActionTitle="Add member"
          primaryActionAriaLabel="Add team member"
          refreshActionTitle="Refresh team"
          refreshActionAriaLabel="Refresh team"
          statusId="broker-team-status-filter"
          statusTestId="broker-team-filter-status"
          statusOptions={TEAM_STATUS_OPTIONS}
          optionalFilters={TEAM_OPTIONAL_FILTERS}
          defaultOptionalKeys={TEAM_DEFAULT_OPTIONAL_KEYS}
          sortFieldOptions={teamSortFieldOptions}
          sortId="broker-team-sort"
          sortTestId="broker-team-sort"
          sortValue={sortToValue(sort)}
          onSortChange={(value) => {
            setPage(1);
            setSort(valueToSort(value));
          }}
        />

        {!loading && !roster.length ? (
          <BrokerEmptyState
            icon={FaUsers}
            title="No team members yet"
            description="Add the first person who will help prepare applications and follow up on individual verification."
            actionLabel="Add member"
            onAction={openAddModal}
          />
        ) : (
          <div className={`broker-applications-table broker-team-table ${hasLastSignInData ? "has-last-sign-in" : ""}`} data-testid="broker-team-grid">
            {loading && admins.length ? (
              <div className="broker-applications-table__loading" role="status" aria-live="polite">
                <FaSpinner className="broker-spin" aria-hidden="true" />
                <span>Refreshing</span>
              </div>
            ) : null}

            {!pagedAdmins.length && loading ? (
              <div className="broker-applications-table__skeleton">
                <BrokerSkeleton rows={7} variant="table" />
              </div>
            ) : null}

            {!pagedAdmins.length && !loading ? (
              <div className="broker-applications-table__empty">
                No team members match your search.
              </div>
            ) : null}

            {pagedAdmins.length ? (
              <div className="broker-applications-table__scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Member</th>
                      <th>Email</th>
                      <th>Access</th>
                      <th>Status</th>
                      <th>Created by</th>
                      <th>Created</th>
                      {hasLastSignInData ? <th>Last sign-in</th> : null}
                      <th className="broker-applications-table__action-header">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedAdmins.map((row, index) => {
                      const adminId = getAdminId(row);
                      const rowTestId = getTeamRowTestId(row, index);
                      return (
                        <tr key={adminId || rowTestId} data-testid={`broker-team-grid-row-${rowTestId}`} data-record-id={adminId || undefined}>
                          <td>
                            <button
                              type="button"
                              className="broker-team-admin-cell broker-team-admin-cell--link"
                              onClick={() => openEditModal(row)}
                              title="Edit member"
                            >
                              <div>
                                <strong>{formatValue(row?.name)}</strong>
                                {adminId === currentAdminId ? <span>You</span> : null}
                              </div>
                            </button>
                          </td>
                          <td>{formatValue(row?.email)}</td>
                          <td>{accessLabel(row)}</td>
                          <td><MemberStatus status={row?.status} /></td>
                          <td><BrokerAdminLabel id={row?.createdBy} fallback="FV Bank team" /></td>
                          <td>{formatDate(row?.createdAt)}</td>
                          {hasLastSignInData ? (
                            <td>{hasDateValue(row?.lastLoginAt) ? formatDate(row.lastLoginAt) : ""}</td>
                          ) : null}
                          <td>{renderTeamActions(row)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}

            {total ? (
              <div className="rw-data-grid__pagination broker-applications-table__pagination" data-testid="broker-team-grid-pagination">
                <Pagination
                  page={page}
                  rowsPerPage={rowsPerPage}
                  total={total}
                  onChange={(nextPage, nextRows) => {
                    setPage(Number(nextPage) || 1);
                    setRowsPerPage(Number(nextRows) || 20);
                  }}
                  testId="broker-team-grid-pagination"
                />
              </div>
            ) : null}
          </div>
        )}
      </section>

      {modalOpen ? (
        <div
          className="broker-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeModal();
          }}
        >
          <section
            className="broker-modal broker-team-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="broker-team-dialog-title"
          >
            <header className="broker-modal__header">
              <div>
                <p>Team access</p>
                <h2 id="broker-team-dialog-title">{editing ? "Edit team member" : "Add team member"}</h2>
              </div>
              <button type="button" className="broker-modal__close" onClick={closeModal} disabled={saving} aria-label="Close dialog">
                <FaTimes aria-hidden="true" />
              </button>
            </header>

            <div className="broker-modal__body">
              {formError ? <BrokerAlert type="error" title="Check the details">{formError}</BrokerAlert> : null}
              {editing ? (
                <div className="broker-team-dialog__summary">
                  <span className="broker-team-avatar broker-team-dialog__summary-avatar">
                    {initials(editing?.name, editing?.email)}
                  </span>
                  <div className="broker-team-dialog__summary-main">
                    <strong>{formatValue(editing?.name)}</strong>
                    <span>{formatValue(editing?.email)}</span>
                  </div>
                  <div className="broker-team-dialog__summary-meta">
                    <MemberStatus status={editing?.status} />
                    <span>{accessLabel(editing)}</span>
                  </div>
                </div>
              ) : (
                <div className="broker-team-dialog__summary broker-team-dialog__summary--new">
                  <span className="broker-team-avatar broker-team-dialog__summary-avatar" aria-hidden="true">
                    <FaUsers aria-hidden="true" />
                  </span>
                  <div className="broker-team-dialog__summary-main">
                    <strong>Add a team member</strong>
                    <span>They will sign in with OTP and can prepare broker-managed applications.</span>
                  </div>
                </div>
              )}

              <div className="broker-team-dialog__form">
                <div className="broker-team-dialog__field">
                  <InputBar
                    id="broker-member-name"
                    testId="broker-team-member-name"
                    className="broker-team-dialog__inputbar"
                    label={<span>Name <span className="broker-field__req">*</span></span>}
                    value={form.name}
                    onChange={(value) => setForm((current) => ({ ...current, name: value }))}
                    disabled={saving}
                    placeholder="Full name"
                    required
                    inputProps={{ autoFocus: true }}
                  />
                </div>

                <div className="broker-team-dialog__field">
                  <InputBar
                    id="broker-member-email"
                    testId="broker-team-member-email"
                    className="broker-team-dialog__inputbar"
                    label={<span>Email <span className="broker-field__req">*</span></span>}
                    type="email"
                    value={form.email}
                    onChange={(value) => setForm((current) => ({ ...current, email: value }))}
                    disabled={saving}
                    placeholder={emailDomain ? `name@${emailDomain}` : "name@example.com"}
                    required
                  />
                  <span className="broker-team-dialog__hint">
                    {emailDomain ? `Must use your @${emailDomain} domain.` : "Set your broker email domain before adding members."}
                  </span>
                </div>

                <div className="broker-team-dialog__field">
                  <SelectDropdown
                    id="broker-member-status"
                    testId="broker-team-member-status"
                    className="broker-team-dialog__inputbar broker-team-dialog__inputbar--select"
                    label={<span>Status <span className="broker-field__req">*</span></span>}
                    options={TEAM_STATUS_OPTIONS}
                    value={normalizeStatus(form.status)}
                    onChange={(value) => setForm((current) => ({ ...current, status: value || "active" }))}
                    disabled={saving || statusLocked}
                    showClear={false}
                    floating={false}
                    panelZIndex={1600}
                    searchPlaceholder="Search status"
                    ariaLabel="Team member status"
                  />
                  <span className="broker-team-dialog__hint">
                    {statusLocked
                      ? statusLockedHint
                      : "Disabled members stay in the team list but cannot use the broker workspace."}
                  </span>
                </div>
              </div>

              <div className="broker-team-dialog__section">
                <h3>{editing ? "Member details" : "Team rules"}</h3>
                <div className="broker-team-dialog__details">
                  {editing ? (
                    <>
                      <DetailItem label="Access">{accessLabel(editing)}</DetailItem>
                      <DetailItem label="Created by">
                        <BrokerAdminLabel id={editing?.createdBy} fallback="FV Bank team" />
                      </DetailItem>
                      <DetailItem label="Created">{formatDateTime(editing?.createdAt)}</DetailItem>
                      {hasDateValue(editing?.lastLoginAt) ? (
                        <DetailItem label="Last sign-in">{formatDateTime(editing.lastLoginAt)}</DetailItem>
                      ) : null}
                      <DetailItem label="Updated by">
                        {editing?.updatedBy ? <BrokerAdminLabel id={editing.updatedBy} fallback="FV Bank team" /> : "Not available"}
                      </DetailItem>
                      <DetailItem label="Updated">{formatDateTime(editing?.updatedAt)}</DetailItem>
                    </>
                  ) : (
                    <>
                      <DetailItem label="Allowed domain">{emailDomain ? `@${emailDomain}` : "Not configured"}</DetailItem>
                      <DetailItem label="Access">Team member</DetailItem>
                      <DetailItem label="Default status">Active</DetailItem>
                    </>
                  )}
                </div>
              </div>
            </div>

            <footer className="broker-modal__footer">
              <button type="button" className="broker-modal__button broker-modal__button--secondary" onClick={closeModal} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="broker-modal__button broker-modal__button--primary" onClick={saveMember} disabled={saving}>
                {saving ? <FaSpinner className="broker-spin" aria-hidden="true" /> : null}
                {editing ? "Save changes" : "Add member"}
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </div>
  );
};

export default BrokerTeam;
